import { matchCalendarDate as localEventDate } from './planningBoardCalendar';
import { buildPlanningBoardOpenSlotsIndicator, type PlanningBoardOpenSlotEntry, type PlanningBoardOpenSlotPosition, type PlanningBoardLineStatus } from './planningBoardOpenSlots';

type PlayerViewMatch = { id: string; match_date: string; match_time?: string | null; timezone?: string | null; league_configuration_id: string; match_status: string; lineup_status?: string | null };

/** Calendar dates are event-local; within each day use scheduled start, never warmup. */
export function groupPlayerViewDates<T extends PlayerViewMatch>(matches: T[], from = '', through = ''): Array<{ date: string; matches: T[] }> {
  const days = new Map<string, T[]>();
  for (const match of matches) {
    const date = localEventDate(match);
    if ((from && date < from) || (through && date > through)) continue;
    const day = days.get(date) || [];
    day.push(match);
    days.set(date, day);
  }
  return [...days].sort(([left], [right]) => left.localeCompare(right)).map(([date, day]) => ({
    date,
    matches: day.sort((left, right) => (left.match_time || '99:99').localeCompare(right.match_time || '99:99') || left.id.localeCompare(right.id)),
  }));
}

export function playerViewStatus(availability: string | undefined, assigned: boolean): string {
  if (availability === undefined) return 'Not rostered';
  if (assigned) return 'Planned';
  if (availability === 'available') return 'Available';
  if (availability === 'not_available') return 'Not Available';
  if (availability === 'maybe') return 'Maybe';
  return 'No response';
}

/** Delegate configured position and default handling to the existing lineup calculation. */
export function playerViewCoverage(match: PlayerViewMatch, positions: PlanningBoardOpenSlotPosition[], entries: PlanningBoardOpenSlotEntry[], lineStatusesByPositionId: Map<string, PlanningBoardLineStatus>) {
  const indicator = buildPlanningBoardOpenSlotsIndicator({ match, positions, entries, lineStatusesByPositionId });
  const required = positions.filter((position) => position.league_configuration_id === match.league_configuration_id && !['default_by_us', 'default_by_opponent'].includes(String(lineStatusesByPositionId.get(position.id))))
    .reduce((count, position) => count + Math.max(1, Number(position.required_player_count) || 1), 0);
  const started = !indicator.isUnstarted || match.lineup_status === 'Draft' || match.lineup_status === 'Published';
  const state = !started ? 'not-started' : indicator.missingSlots.length ? 'incomplete' : 'complete';
  return { state, missingSlots: indicator.missingSlots, filled: required - indicator.missingSlots.length, warn: match.match_status !== 'Completed' && state === 'incomplete' };
}

/** Focus teams select players; their schedule includes every accessible active membership. */
export function playerViewMatchesForPlayers<T extends PlayerViewMatch & { team_id: string }>(
  matches: T[],
  memberships: Array<{ team_id: string; player_id: string; is_active: boolean; roster_status?: string | null }>,
  playerIds: string[],
  includeCompleted: boolean,
  today: string,
): T[] {
  const displayedPlayers = new Set(playerIds);
  const teamIds = new Set(memberships
    .filter((membership) => membership.is_active && membership.roster_status !== 'inactive' && displayedPlayers.has(membership.player_id))
    .map((membership) => membership.team_id));
  return matches.filter((match) => teamIds.has(match.team_id)
    && match.match_status !== 'Cancelled'
    && (includeCompleted || (match.match_status !== 'Completed' && localEventDate(match) >= today)));
}
